/**
 * @description 季报主页面组件
 * @date 2025-01-24
 * @author lv.chen
 */
import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useHistory } from 'umi';
import Loading from '@home/components/Loading';
import ContextMenu from '@home/components/ContextMenu';
import Image from '@user-center/components/Image';
import DownloadButton from '@user-center/components/DownloadButton';
import NewFeatureTipsBubble from '@user-center/components/NewFeatureTipsBubble';
import QuarterSelector from './components/QuarterSelector';
import QuarterReportHeader from './components/QuarterReportHeader/index';
import ChartTrend from './components/ChartTrend';
import HighlightMoment from './components/HighlightMoment/index';
import UserPrefer from './components/UserPrefer';
import QRCode from './components/QRCode';
import { useLocation } from 'umi';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import usePageLog, { reportButtonLog } from '@user-center/_hooks/usePageLog';
import useQuarterReport from './_hooks/useQuarterReport';
import useQuarterAvailability from './_hooks/useQuarterAvailability';
import { formatDateStr } from '@home/utils/date';
import { downloadFile } from '@home/utils/file';
import { screenshot } from '@user-center/utils/dom';
import { LANGUAGE_EN } from '@home/_config/config';
import { TIPS_VIEW_STATE_KEY_MAP } from '@user-center/_config/tipsViewState';
import { getYearFromDate } from './utils';
import { GENRE_NAME_MAP } from './constants';

import type { IContextMenuItem } from '@home/components/ContextMenu/types';

import IMG_LOGO_LEVEL from '@user-center/assets/images/common/logo-level.png';
import IMG_LOGO from '@user-center/assets/images/common/logo.png';
import IMG_LOGO_P from '@user-center/assets/images/common/logo-p.png';

import './index.scss';

interface IQuarterReportProps extends IBaseProps {
  // 可以根据需要添加其他props
}

const getQuarterReportFileName = (date?: string): string => {
  const reportDateFormated = `Q${formatDateStr(date, 'Q YYYY')}`;
  return `Quarter Report ${reportDateFormated}`;
};

/**
 * 季报主页面组件
 */
const QuarterReport: React.FC<IQuarterReportProps> = (props) => {
  const { style, className } = props;

  const { locale, formatMessage } = useIntlExtend();
  const isEN = locale === LANGUAGE_EN;

  /**
   * @date 2025-10-04
   * @description 右键菜单项配置
   * @author lv.chen
   */
  const menuItems: IContextMenuItem[] = [
    {
      key: 'saveImage',
      label: formatMessage({ id: 'dbh_save_img' }) as string,
    },
    {
      key: 'copyImage',
      label: formatMessage({ id: 'dbh_copy_img' }) as string,
    },
  ];

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date') || '';
  const openId = searchParams.get('openId') || '';
  const history = useHistory();

  // Get quarter availability from /quarter_report/dates API
  const { loading: availabilityLoading, options, defaultDate } = useQuarterAvailability(openId);

  // Determine effective date (default to backend-recommended date if not specified)
  const effectiveDate = date || defaultDate || '';

  // 从 effectiveDate 解析年份，用于动态加载对应年份的样式和组件
  const year = getYearFromDate(effectiveDate);

  const { loading, isNoData, reportDate, report } = useQuarterReport({
    date: effectiveDate,
    openId,
  });

  const quarterChars = loading ? '' : `Q${formatDateStr(reportDate, 'Q')}`;

  // 获取最爱游戏列表
  const favouriteGames = report?.favourite_games || [];
  // 获取最爱品类
  const favouriteGenre = report?.favourite_genre?.toUpperCase() || '';
  // 获取最常访问类型
  const favouriteEntityType = report?.favourite_entity_type || '';
  // 是否展示游戏偏好档案模块
  const showUserProfile = !!favouriteGames?.length || !!favouriteGenre || !!favouriteEntityType;

  const quarterReportPageNode = useRef<HTMLDivElement>(null);

  const [downloadLoading, setDownloadLoading] = useState(false);

  /**
   * @date 2025-10-10
   * @description 处理下载按钮点击事件
   * @author lv.chen
   */
  const handleDownload = async (logKey: string, fileName: string) => {
    if (!quarterReportPageNode.current) {
      return;
    }

    reportButtonLog(logKey, { extInfo: fileName });

    const file = await screenshot(quarterReportPageNode.current, fileName);
    if (file) {
      downloadFile(file, fileName);
    }
  };

  /**
   * @date 2025-10-04
   * @description 处理右键菜单点击事件
   * @author lv.chen
   */
  const handleMenuClick = async (key: string) => {
    if (!quarterReportPageNode.current) {
      return;
    }

    try {
      const fileName = getQuarterReportFileName(reportDate);
      if (key === 'saveImage') {
        void handleDownload('userDataCenterQuarterReportSaveImage', fileName);
      } else if (key === 'copyImage') {
        reportButtonLog('userDataCenterQuarterReportCopyImage', { extInfo: fileName });
        // 复制图片
        const file = await screenshot(quarterReportPageNode.current, fileName);
        if (file) {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': file,
            }),
          ]);
        }
      }
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleQuarterChange = (newDate: string) => {
    const targetPath = '/user-center/user-data-center/quarter-report';
    history.push({
      pathname: targetPath,
      search: `?date=${newDate}${openId ? `&openId=${openId}` : ''}`,
    });
  };

  usePageLog('userDataCenterQuarterReport');

  return (
    <>
      <div className="dbu-quarter-report-toolbar">
        <QuarterSelector
          currentDate={effectiveDate}
          options={options}
          onQuarterChange={handleQuarterChange}
        />
        <DownloadButton
          disabled={false}
          loading={downloadLoading}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => {
            try {
              setDownloadLoading(true);
              const fileName = getQuarterReportFileName(reportDate);
              await handleDownload('userDataCenterQuarterReportDownloadButton', fileName);
            } finally {
              setDownloadLoading(false);
            }
          }}
        />
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises*/}
      <ContextMenu menuItems={menuItems} onMenuClick={handleMenuClick}>
        <div
          ref={quarterReportPageNode}
          style={style}
          className={classnames(`year-${year}`, className)}
        >
        <div
          className={classnames('dbu-quarter-report quarter-report', {
            'is-en': isEN,
            'is-cn': !isEN,
          })}
        >
          {loading && <Loading />}
          {!loading && isNoData && <div className="quarter-report__placeholder"></div>}
          {!loading && !isNoData && (
            <>
              {/* 右键新功能强提示 */}
              <NewFeatureTipsBubble
                newFeatureTipsKey={TIPS_VIEW_STATE_KEY_MAP.QUARTER_REPORT_CONTEXT_MENU_TIPS}
                newFeatureTipsContent={formatMessage({ id: 'dbu_quarter_report_context_menu_tips' }) as string}
                newFeatureTipsReadyShow={!loading && !isNoData}
                placement="left-start"
                overlayStyle={{ zIndex: 1 }}
              >
                <div className="dbu-quarter-report__new-feature-tips-trigger"></div>
              </NewFeatureTipsBubble>
              <QuarterReportHeader year={year} isEN={isEN} reportDate={reportDate} report={report} />
              <div className="quarter-report-content">
                {/* 季度活跃趋势 */}
                <ChartTrend quarterChars={quarterChars} report={report} year={year} />

                {/* 季度高光时刻 */}
                <HighlightMoment year={year} quarterChars={quarterChars} report={report} />

                {/* 游戏偏好档案  用户没有访问游戏需隐藏*/}
                {showUserProfile && (
                  <UserPrefer
                    year={year}
                    favouriteGames={favouriteGames}
                    favouriteGenre={!GENRE_NAME_MAP[favouriteGenre] ? '' : favouriteGenre}
                    favouriteEntityType={favouriteEntityType}
                  />
                )}

                {/* 扫码前往 */}
                <div className="quarter-report-scan">
                  <div className="quarter-report-scan-info">
                    <div>{formatMessage({ id: 'dbu_quarter_report_scan_to_miniprogram' })}</div>
                    <div>
                      {formatMessage({ id: 'dbu_quarter_report_scan_instruction_1' })}
                      <span className="icon-user"></span>-{' '}
                      {formatMessage({ id: 'dbu_quarter_report_scan_user_center' })} -{' '}
                      {formatMessage({ id: 'dbu_quarter_report_scan_quarterly_mbti' })}
                    </div>
                    <div>{formatMessage({ id: 'dbu_quarter_report_scan_instruction_3' })}</div>
                  </div>
                  <div className="quarter-report-scan-pic">
                    <QRCode />
                  </div>
                </div>
              </div>

              <div className="page-footer-logos">
                <div className="page-footer-logos-item">
                  <Image className="page-footer-logos-img" src={IMG_LOGO_LEVEL} alt="" />
                </div>
                <div className="page-footer-logos-item">
                  <Image className="page-footer-logos-img" src={IMG_LOGO} alt="" />
                </div>
                <div className="page-footer-logos-item">
                  <Image className="page-footer-logos-img" src={IMG_LOGO_P} alt="" />
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      </ContextMenu>
    </>
  );
};

export default QuarterReport;
